import {
    Badge,
    Box,
    Button,
    Center,
    Flex,
    Icon,
    Input,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tfoot,
    Tr,
    Spinner,
    useDisclosure,
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    FormControl,
    FormErrorMessage,
    SimpleGrid,
    Tooltip,
    useColorModeValue,
} from '@chakra-ui/react';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import Card from 'components/card/Card';
import { useKeycloak } from '@react-keycloak/web';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { MdOutlineDeleteOutline, MdOutlineEdit, MdLockReset, MdOutlineDownload } from 'react-icons/md';
import { FaSync } from 'react-icons/fa';
import * as EmployeeService from 'services/EmployeeService';
import { Employee } from 'utils/interfaces';
import { isAdmin } from 'utils/auth-utils';

// Translates the backend's raw validation message (English, from EmployeeController.handleBadRequest)
// into something an admin actually understands. Falls back to showing the raw message for any rule
// added later that this doesn't specifically recognize, rather than hiding it behind a generic error.
const describeEmployeeValidationError = (message: string): string => {
    if (message.includes('already exists')) {
        return 'Esiste già un dipendente con questa email.';
    }
    return message || 'Dati non validi. Controlla i campi e riprova.';
};

export default function Employees() {
    const { keycloak } = useKeycloak();
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [pageNumber, setPageNumber] = useState(0);
    const [pageSize] = useState(25);
    const [totalPages, setTotalPages] = useState(0);
    const [nameFilter, setNameFilter] = useState('');
    const [emailFilter, setEmailFilter] = useState('');
    const [phoneFilter, setPhoneFilter] = useState('');
    const [typedNameFilter, setTypedNameFilter] = useState('');
    const [typedEmailFilter, setTypedEmailFilter] = useState('');
    const [typedPhoneFilter, setTypedPhoneFilter] = useState('');
    const [inputPageNumber, setInputPageNumber] = useState('');

    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const [deleteEmployeeId, setDeleteEmployeeId] = useState<number | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const cancelRef = useRef(null);

    const { isOpen: isResetPasswordOpen, onOpen: onResetPasswordOpen, onClose: onResetPasswordClose } = useDisclosure();
    const [resetPasswordEmployeeId, setResetPasswordEmployeeId] = useState<number | null>(null);
    const [isResettingPassword, setIsResettingPassword] = useState(false);
    const resetPasswordCancelRef = useRef(null);

    // Create Modal
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [dateOfBirth, setDateOfBirth] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [firstNameError, setFirstNameError] = useState('');
    const [lastNameError, setLastNameError] = useState('');
    const [emailError, setEmailError] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [dateError, setDateError] = useState('');

    // Edit Modal
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingEmployeeId, setEditingEmployeeId] = useState<number | null>(null);
    const [editFirstName, setEditFirstName] = useState('');
    const [editLastName, setEditLastName] = useState('');
    const [editEmail, setEditEmail] = useState('');
    const [editPhoneNumber, setEditPhoneNumber] = useState('');
    const [editDateOfBirth, setEditDateOfBirth] = useState('');
    const [editIsSubmitting, setEditIsSubmitting] = useState(false);
    const [editFirstNameError, setEditFirstNameError] = useState('');
    const [editLastNameError, setEditLastNameError] = useState('');
    const [editEmailError, setEditEmailError] = useState('');
    const [editPhoneError, setEditPhoneError] = useState('');
    const [editDateError, setEditDateError] = useState('');

    const jwtToken = keycloak?.token;
    const adminUser = isAdmin(keycloak!);

    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
    const shadow = useColorModeValue(
        '14px 17px 40px 4px rgba(112, 144, 176, 0.18)',
        '14px 17px 40px 4px rgba(112, 144, 176, 0.06)'
    );

    const columnHelper = createColumnHelper<Employee>();

    const columns = [
        columnHelper.accessor('firstName', {
            id: 'firstName',
            header: () => (
                <Text
                    justifyContent='space-between'
                    align='center'
                    marginLeft='auto'
                    marginRight='auto'
                    fontSize={{ sm: '10px', lg: '14px' }}
                    textTransform="none"
                    color='gray.400'
                    textAlign="center"
                >                    Nome
                </Text>
            ),
            cell: (info: any) => (
                <Text color={textColor} fontSize='xs' fontWeight='700' textAlign="center" marginLeft='auto' marginRight='auto'>
                    {info.getValue()}
                </Text>
            ),
        }),
        columnHelper.accessor('lastName', {
            id: 'lastName',
            header: () => (
                <Text
                    justifyContent='space-between'
                    align='center'
                    marginLeft='auto'
                    marginRight='auto'
                    fontSize={{ sm: '10px', lg: '14px' }}
                    textTransform="none"
                    color='gray.400'
                    textAlign="center"
                >                    Cognome
                </Text>
            ),
            cell: (info: any) => (
                <Text color={textColor} fontSize='xs' fontWeight='700' textAlign="center" marginLeft='auto' marginRight='auto'>
                    {info.getValue()}
                </Text>
            ),
        }),
        columnHelper.accessor('email', {
            id: 'email',
            header: () => (
                <Text
                    justifyContent='space-between'
                    align='center'
                    marginLeft='auto'
                    marginRight='auto'
                    fontSize={{ sm: '10px', lg: '14px' }}
                    textTransform="none"
                    color='gray.400'
                    textAlign="center"
                >                    Email
                </Text>
            ),
            cell: (info: any) => (
                <Text color={textColor} fontSize='xs' fontWeight='700' textAlign="center" marginLeft='auto' marginRight='auto'>
                    {info.getValue()}
                </Text>
            ),
        }),
        columnHelper.accessor('phoneNumber', {
            id: 'phoneNumber',
            header: () => (
                <Text
                    justifyContent='space-between'
                    align='center'
                    marginLeft='auto'
                    marginRight='auto'
                    fontSize={{ sm: '10px', lg: '14px' }}
                    textTransform="none"
                    color='gray.400'
                    textAlign="center"
                >                    Telefono
                </Text>
            ),
            cell: (info: any) => (
                <Text color={textColor} fontSize='xs' fontWeight='700' textAlign="center" marginLeft='auto' marginRight='auto'>
                    {info.getValue() || '-'}
                </Text>
            ),
        }),
        columnHelper.accessor('dateOfBirth', {
            id: 'dateOfBirth',
            header: () => (
                <Text
                    justifyContent='space-between'
                    align='center'
                    marginLeft='auto'
                    marginRight='auto'
                    fontSize={{ sm: '10px', lg: '14px' }}
                    textTransform="none"
                    color='gray.400'
                    textAlign="center"
                >                    Data Nascita
                </Text>
            ),
            cell: (info: any) => (
                <Text color={textColor} fontSize='xs' fontWeight='700' textAlign="center" marginLeft='auto' marginRight='auto'>
                    {info.getValue() || '-'}
                </Text>
            ),
        }),
        columnHelper.accessor('hasSignature', {
            id: 'hasSignature',
            header: () => (
                <Text
                    justifyContent='space-between'
                    align='center'
                    marginLeft='auto'
                    marginRight='auto'
                    fontSize={{ sm: '10px', lg: '14px' }}
                    textTransform="none"
                    color='gray.400'
                    textAlign="center"
                >                    Firma
                </Text>
            ),
            cell: (info: any) => (
                <Flex justifyContent='center' align="center" gap="2">
                    {info.getValue() ? (
                        <>
                            <Badge colorScheme="green" fontSize="10px" borderRadius="full" px="2">
                                Firma presente
                            </Badge>
                            <Tooltip label="Scarica firma" fontSize="md">
                                <Button
                                    onClick={() => handleDownloadSignature(info.row.original)}
                                    _hover={{ bg: 'gray.200' }} _active={{ bg: 'gray.100' }} _focus={{ boxShadow: 'none' }}
                                    color="white"
                                    style={{ height: '30px', padding: '0px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}
                                    fontSize='xs'
                                >
                                    <Icon as={MdOutlineDownload} w={4} h={4} color="black" />
                                </Button>
                            </Tooltip>
                        </>
                    ) : (
                        <Badge colorScheme="yellow" fontSize="10px" borderRadius="full" px="2">
                            Firma mancante
                        </Badge>
                    )}
                </Flex>
            ),
        }),
        {
            id: 'actions',
            header: () => (
                <Text
                    justifyContent='space-between'
                    align='center'
                    marginLeft='auto'
                    marginRight='auto'
                    fontSize={{ sm: '10px', lg: '14px' }}
                    textTransform="none"
                    color='gray.400'
                    textAlign="center"
                >                    Azioni
                </Text>
            ),
            cell: (info: any) => (
                <Flex justifyContent='center'>
                    <Tooltip label="Modifica" fontSize="md">
                        <Button
                            onClick={() => openEditModal(info.row.original)}
                            _hover={{ bg: 'gray.200' }} _active={{ bg: 'gray.100' }} _focus={{ boxShadow: 'none' }}
                            color="white"
                            style={{ height: '30px', padding: '0px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}
                            fontSize='xs' mx="1"
                        >
                            <Icon as={MdOutlineEdit} w={4} h={4} color="black" />
                        </Button>
                    </Tooltip>
                    <Tooltip label="Reset Password" fontSize="md">
                        <Button
                            onClick={() => handleResetPasswordClick(info.row.original.id)}
                            _hover={{ bg: 'gray.200' }} _active={{ bg: 'gray.100' }} _focus={{ boxShadow: 'none' }}
                            color="white"
                            style={{ height: '30px', padding: '0px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}
                            fontSize='xs' mx="1"
                        >
                            <Icon as={MdLockReset} w={4} h={4} color="black" />
                        </Button>
                    </Tooltip>
                    <Tooltip label="Elimina" fontSize="md">
                        <Button
                            onClick={() => handleDeleteClick(info.row.original.id)}
                            _hover={{ bg: 'gray.200' }} _active={{ bg: 'gray.100' }} _focus={{ boxShadow: 'none' }}
                            color="white"
                            style={{ height: '30px', padding: '0px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}
                            fontSize='xs' mx="1"
                        >
                            <Icon as={MdOutlineDeleteOutline} w={4} h={4} color="black" />
                        </Button>
                    </Tooltip>
                </Flex>
            ),
        },
    ];

    const table = useReactTable({
        columns,
        data: employees,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        debugTable: false,
        debugHeaders: false,
        debugColumns: false,
    });

    console.log('[Employees Page] adminUser:', adminUser, 'keycloak.authenticated:', keycloak?.authenticated);

    const loadEmployees = async (page = 0, name = nameFilter, email = emailFilter, phoneNumber = phoneFilter) => {
        if (!jwtToken) return;

        setIsLoading(true);
        const response = await EmployeeService.getAllPaginated(
            jwtToken,
            () => {
                keycloak?.logout();
            },
            page,
            pageSize,
            { name, email, phoneNumber }
        );

        if (response) {
            setEmployees(response.content);
            setPageNumber(page);
            setTotalPages(response.totalPages);
        }

        setIsLoading(false);
    };

    // 500ms debounce: commit the typed filters to the applied ones only once the user stops typing.
    useEffect(() => {
        const handler = setTimeout(() => {
            setNameFilter(typedNameFilter);
            setEmailFilter(typedEmailFilter);
            setPhoneFilter(typedPhoneFilter);
        }, 500);
        return () => clearTimeout(handler);
    }, [typedNameFilter, typedEmailFilter, typedPhoneFilter]);

    useEffect(() => {
        loadEmployees(0, nameFilter, emailFilter, phoneFilter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nameFilter, emailFilter, phoneFilter]);

    const handleDownloadSignature = async (employee: Employee) => {
        if (!jwtToken) return;

        const blob = await EmployeeService.downloadSignatureFile(
            jwtToken,
            () => {
                keycloak?.logout();
            },
            employee.id
        );

        if (!blob) {
            toast.error('Errore durante il download della firma');
            return;
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `firma_${employee.firstName}_${employee.lastName}.png`);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const handlePrevious = () => {
        if (pageNumber > 0) {
            loadEmployees(pageNumber - 1);
        }
    };

    const handleNext = () => {
        if (pageNumber < totalPages - 1) {
            loadEmployees(pageNumber + 1);
        }
    };

    const handleDeleteClick = (id: number) => {
        setDeleteEmployeeId(id);
        onDeleteOpen();
    };

    const handleResetPasswordClick = (id: number) => {
        setResetPasswordEmployeeId(id);
        onResetPasswordOpen();
    };

    const handleConfirmResetPassword = async () => {
        if (resetPasswordEmployeeId === null || !jwtToken) return;

        setIsResettingPassword(true);
        const success = await EmployeeService.resetPassword(
            jwtToken,
            () => { keycloak?.logout(); },
            resetPasswordEmployeeId
        );
        setIsResettingPassword(false);

        if (success) {
            toast.success('Email di reset inviata con successo');
            onResetPasswordClose();
            setResetPasswordEmployeeId(null);
        } else {
            toast.error('Errore durante il reset della password');
        }
    };

    const handleConfirmDelete = async () => {
        if (deleteEmployeeId === null || !jwtToken) return;

        setIsDeleting(true);
        const success = await EmployeeService.deleteEmployee(
            jwtToken,
            () => {
                keycloak?.logout();
            },
            deleteEmployeeId
        );

        setIsDeleting(false);

        if (success) {
            toast.success('Dipendente eliminato');
            onDeleteClose();
            setDeleteEmployeeId(null);
            loadEmployees(pageNumber);
        } else {
            toast.error('Errore nell\'eliminazione del dipendente');
        }
    };

    const validateEmail = (emailValue: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(emailValue);
    };

    const validatePhoneNumber = (phone: string): boolean => {
        if (!phone) return true;
        const phoneRegex = /^\+?[0-9\s\-()]+$/;
        return phoneRegex.test(phone);
    };

    const validateDateOfBirth = (date: string): boolean => {
        if (!date) return true;
        const dateObj = new Date(date);
        return dateObj <= new Date();
    };

    const validateForm = (): boolean => {
        let isValid = true;
        setFirstNameError('');
        setLastNameError('');
        setEmailError('');
        setPhoneError('');
        setDateError('');

        if (!firstName.trim()) {
            setFirstNameError('Nome è obbligatorio');
            isValid = false;
        }

        if (!lastName.trim()) {
            setLastNameError('Cognome è obbligatorio');
            isValid = false;
        }

        if (!email.trim()) {
            setEmailError('Email è obbligatoria');
            isValid = false;
        } else if (!validateEmail(email)) {
            setEmailError('Email non valida');
            isValid = false;
        }

        if (phoneNumber && !validatePhoneNumber(phoneNumber)) {
            setPhoneError('Formato telefono non valido. Es: +39 328 1234567');
            isValid = false;
        }

        if (dateOfBirth && !validateDateOfBirth(dateOfBirth)) {
            setDateError('La data di nascita non può essere futura');
            isValid = false;
        }

        return isValid;
    };

    const openCreateModal = () => {
        setIsCreateModalOpen(true);
    };

    const closeCreateModal = () => {
        setIsCreateModalOpen(false);
        setFirstName('');
        setLastName('');
        setEmail('');
        setPhoneNumber('');
        setDateOfBirth('');
        setFirstNameError('');
        setLastNameError('');
        setEmailError('');
        setPhoneError('');
        setDateError('');
    };

    const handleCreateEmployee = async () => {
        if (!validateForm() || !jwtToken) return;

        setIsSubmitting(true);
        const result = await EmployeeService.createEmployee(
            jwtToken,
            () => {
                keycloak?.logout();
            },
            {
                firstName,
                lastName,
                email,
                phoneNumber: phoneNumber || undefined,
                dateOfBirth: dateOfBirth || undefined,
            }
        );

        setIsSubmitting(false);

        switch (result.status) {
            case 'success':
                toast.success('Dipendente creato con successo!');
                closeCreateModal();
                setTypedNameFilter('');
                setTypedEmailFilter('');
                setTypedPhoneFilter('');
                setNameFilter('');
                setEmailFilter('');
                setPhoneFilter('');
                loadEmployees(0, '', '', '');
                break;
            case 'validation_error':
                toast.error(describeEmployeeValidationError(result.message));
                break;
            default:
                toast.error('Errore nella creazione del dipendente');
        }
    };

    const openEditModal = (employee: Employee) => {
        setEditingEmployeeId(employee.id);
        setEditFirstName(employee.firstName);
        setEditLastName(employee.lastName);
        setEditEmail(employee.email);
        setEditPhoneNumber(employee.phoneNumber || '');
        setEditDateOfBirth(employee.dateOfBirth || '');
        setEditFirstNameError('');
        setEditLastNameError('');
        setEditEmailError('');
        setEditPhoneError('');
        setEditDateError('');
        setIsEditModalOpen(true);
    };

    const closeEditModal = () => {
        setIsEditModalOpen(false);
        setEditingEmployeeId(null);
        setEditFirstName('');
        setEditLastName('');
        setEditEmail('');
        setEditPhoneNumber('');
        setEditDateOfBirth('');
        setEditFirstNameError('');
        setEditLastNameError('');
        setEditEmailError('');
        setEditPhoneError('');
        setEditDateError('');
    };

    const validateEditForm = (): boolean => {
        let isValid = true;
        setEditFirstNameError('');
        setEditLastNameError('');
        setEditEmailError('');
        setEditPhoneError('');
        setEditDateError('');

        if (!editFirstName.trim()) {
            setEditFirstNameError('Nome è obbligatorio');
            isValid = false;
        }

        if (!editLastName.trim()) {
            setEditLastNameError('Cognome è obbligatorio');
            isValid = false;
        }

        if (!editEmail.trim()) {
            setEditEmailError('Email è obbligatoria');
            isValid = false;
        } else if (!validateEmail(editEmail)) {
            setEditEmailError('Email non valida');
            isValid = false;
        }

        if (editPhoneNumber && !validatePhoneNumber(editPhoneNumber)) {
            setEditPhoneError('Formato telefono non valido. Es: +39 328 1234567');
            isValid = false;
        }

        if (editDateOfBirth && !validateDateOfBirth(editDateOfBirth)) {
            setEditDateError('La data di nascita non può essere futura');
            isValid = false;
        }

        return isValid;
    };

    const handleEditEmployee = async () => {
        if (!validateEditForm() || !jwtToken || editingEmployeeId === null) return;

        setEditIsSubmitting(true);
        const result = await EmployeeService.updateEmployee(
            jwtToken,
            () => {
                keycloak?.logout();
            },
            editingEmployeeId,
            {
                firstName: editFirstName || undefined,
                lastName: editLastName || undefined,
                email: editEmail || undefined,
                phoneNumber: editPhoneNumber || undefined,
                dateOfBirth: editDateOfBirth || undefined,
            }
        );

        setEditIsSubmitting(false);

        switch (result.status) {
            case 'success':
                toast.success('Dipendente modificato con successo!');
                closeEditModal();
                loadEmployees(pageNumber);
                break;
            case 'validation_error':
                toast.error(describeEmployeeValidationError(result.message));
                break;
            default:
                toast.error('Errore nella modifica del dipendente');
        }
    };


    if (!adminUser) {
        return (
            <Center minH="100vh">
                <Text fontSize="xl" color="red.500" textAlign="center">
                    Solo gli amministratori possono accedere a questa sezione.<br />
                </Text>
            </Center>
        );
    }

    return (
        <Box pt={{ base: '40px', md: '80px', xl: '78px' }} pb="40px" pr="40px">
            <Flex justifyContent="space-between" alignItems="center" mb="30px" flexWrap="wrap" gap="10px">
                <Text fontSize="2xl" fontWeight="bold" color="#9FB50C">
                    Gestione Dipendenti
                </Text>
            </Flex>

            <SimpleGrid
                px="10px"
                mx="10px"
                width={{ base: '100%', md: '96%' }}
                marginLeft={{ base: '0px', md: '0px' }}
                py='10px'
                backgroundColor='white'
                borderRadius='20px'
                boxShadow={shadow}
                minChildWidth={{ base: '160px', sm: '200px' }}
                spacing={0}
                mb='20px'
                alignItems="center"
            >
                <Card py='10px' px='0px'>
                    <Flex h='100%' align={{ base: 'center', xl: 'start' }} justify={{ base: 'start', xl: 'start' }}>
                        <Flex direction='column' my='auto'>
                            <Text lineHeight='100%' fontWeight="bold" fontSize={{ base: 'sm' }} mb={2}>
                                Nome e Cognome
                            </Text>
                            <Flex mt='3%'>
                                <Input
                                    type="text"
                                    borderRadius='6px'
                                    size="sm"
                                    placeholder="Nome dipendente"
                                    value={typedNameFilter}
                                    onChange={(e) => setTypedNameFilter(e.target.value)}
                                    focusBorderColor="gray.500"
                                    _hover={{ borderColor: 'gray.500' }}
                                />
                            </Flex>
                        </Flex>
                    </Flex>
                </Card>
                <Card py='10px' px='0px'>
                    <Flex h='100%' align={{ base: 'center', xl: 'start' }} justify={{ base: 'start', xl: 'start' }}>
                        <Flex direction='column' my='auto'>
                            <Text lineHeight='100%' fontWeight="bold" fontSize={{ base: 'sm' }} mb={2}>
                                Email
                            </Text>
                            <Flex mt='3%'>
                                <Input
                                    type="text"
                                    borderRadius='6px'
                                    size="sm"
                                    placeholder="Email dipendente"
                                    value={typedEmailFilter}
                                    onChange={(e) => setTypedEmailFilter(e.target.value)}
                                    focusBorderColor="gray.500"
                                    _hover={{ borderColor: 'gray.500' }}
                                />
                            </Flex>
                        </Flex>
                    </Flex>
                </Card>
                <Card py='10px' px='0px'>
                    <Flex h='100%' align={{ base: 'center', xl: 'start' }} justify={{ base: 'start', xl: 'start' }}>
                        <Flex direction='column' my='auto'>
                            <Text lineHeight='100%' fontWeight="bold" fontSize={{ base: 'sm' }} mb={2}>
                                Numero di Telefono
                            </Text>
                            <Flex mt='3%'>
                                <Input
                                    type="text"
                                    borderRadius='6px'
                                    size="sm"
                                    placeholder="Telefono dipendente"
                                    value={typedPhoneFilter}
                                    onChange={(e) => setTypedPhoneFilter(e.target.value)}
                                    focusBorderColor="gray.500"
                                    _hover={{ borderColor: 'gray.500' }}
                                />
                            </Flex>
                        </Flex>
                    </Flex>
                </Card>
            </SimpleGrid>

            <Card
                boxShadow={shadow}
                flexDirection='column'
                width={{ base: '100%', md: '96%' }}
                marginLeft={{ base: '0px', md: '0px' }}
                mx="10px"
                pt='25px'
                overflowX={{ sm: 'scroll', lg: 'hidden' }}
            >
                <Flex px='25px' mb="20px" justifyContent='space-between' align='center'>
                    <Text color="#9FB50C" fontSize='22px' fontWeight='700' lineHeight='100%'>
                        Lista Dipendenti
                    </Text>
                    <Flex>
                        <Button
                            onClick={openCreateModal}
                            _hover={{ bg: 'gray.700' }}
                            _active={{ bg: 'gray.600' }}
                            _focus={{ boxShadow: 'none' }}
                            backgroundColor='#688CC8'
                            color="white"
                            style={{
                                height: '30px',
                                padding: '0px 12px',
                                borderRadius: '6px',
                                border: 'none',
                                cursor: 'pointer',
                            }}
                            fontSize='xs'
                            mx="1"
                        >
                            + Aggiungi
                        </Button>
                        <Button
                            onClick={() => loadEmployees(pageNumber)}
                            variant="ghost"
                            colorScheme="blue"
                            mx="1"
                            p={2}
                            _hover={{ bg: 'gray.200' }}
                        >
                            <Icon as={FaSync} w={5} h={5} />
                        </Button>
                    </Flex>
                </Flex>

                <Box overflowX="auto">
                    <Table variant='simple' color='gray.500' mb='24px' mt="12px" w='100%' size="sm">
                        <Thead>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <Tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <Th
                                            key={header.id}
                                            colSpan={header.colSpan}
                                            pe='10px'
                                            borderColor={borderColor}
                                            paddingInline="0px"
                                        >
                                            <Flex
                                                justifyContent='space-between'
                                                align='center'
                                                fontSize={{ sm: '10px', lg: '12px' }}
                                                color='gray.400'>
                                                {flexRender(header.column.columnDef.header, header.getContext())}{{
                                                    asc: '',
                                                    desc: '',
                                                }[header.column.getIsSorted() as string] ?? null}
                                            </Flex>
                                        </Th>
                                    ))}
                                </Tr>
                            ))}
                        </Thead>
                        <Tbody>
                            {isLoading ? (
                                Array.from({ length: 15 }).map((_, rowIndex) => (
                                    <Tr key={`loading-row-${rowIndex}`}>
                                        {columns.map((column, columnIndex) => (
                                            <Td
                                                key={`loading-cell-${rowIndex}-${columnIndex}`}
                                                pb='6px'
                                                fontSize={{ sm: '14px' }}
                                                minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                                                borderColor='transparent'
                                                textAlign="center"
                                            >
                                                {rowIndex === 0 ? <Spinner size="sm" color="gray.500" /> : null}
                                            </Td>
                                        ))}
                                    </Tr>
                                ))
                            ) : employees.length > 0 ? (
                                <>
                                    {table.getRowModel().rows.map((row) => (
                                        <Tr
                                            key={row.id}
                                            _hover={{ bg: 'gray.200' }}
                                        >
                                            {row.getVisibleCells().map((cell) => (
                                                <Td
                                                    pb='2px'
                                                    key={cell.id}
                                                    fontSize={{ sm: '14px' }}
                                                    minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                                                    borderColor='transparent'
                                                >
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </Td>
                                            ))}
                                        </Tr>
                                    ))}
                                </>
                            ) : (
                                <Tr>
                                    <Td colSpan={columns.length} textAlign="center">
                                        Nessun dipendente
                                    </Td>
                                </Tr>
                            )}
                        </Tbody>
                        <Tfoot w="100%">
                            <Tr>
                                <Td colSpan={columns.length} textAlign="center">
                                    <Flex justifyContent="center" alignItems="center" mt={4}>
                                        <Button
                                            onClick={handlePrevious}
                                            isDisabled={pageNumber === 0}
                                            height="100%"
                                        >
                                            Precedente
                                        </Button>
                                        <Text mx={2}>
                                            Pagina {pageNumber + 1} di {totalPages}
                                        </Text>
                                        <Button
                                            onClick={handleNext}
                                            isDisabled={pageNumber === totalPages - 1}
                                            height="100%"
                                        >
                                            Successiva
                                        </Button>
                                        <Flex alignItems="center" mx={2}>
                                            <Input
                                                type="number"
                                                placeholder="Pagina"
                                                value={inputPageNumber}
                                                onChange={(e) => setInputPageNumber(e.target.value)}
                                                width="90px"
                                                mx={2}
                                            />
                                            <Button
                                                onClick={() => {
                                                    const pageNum = parseInt(inputPageNumber, 10);
                                                    if (!isNaN(pageNum) && pageNum > 0 && pageNum <= totalPages) {
                                                        loadEmployees(pageNum - 1);
                                                        setInputPageNumber('');
                                                    }
                                                }}
                                            >
                                                Vai
                                            </Button>
                                        </Flex>
                                    </Flex>
                                </Td>
                            </Tr>
                        </Tfoot>
                    </Table>
                </Box>
            </Card>

            <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelRef} onClose={onDeleteClose}>
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Elimina Dipendente
                        </AlertDialogHeader>
                        <AlertDialogBody>Vuoi eliminare questo dipendente?</AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onDeleteClose}>
                                Annulla
                            </Button>
                            <Button
                                colorScheme="red"
                                onClick={handleConfirmDelete}
                                ml={3}
                                isLoading={isDeleting}
                            >
                                Elimina
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

            <AlertDialog isOpen={isResetPasswordOpen} leastDestructiveRef={resetPasswordCancelRef} onClose={onResetPasswordClose}>
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Reset Password
                        </AlertDialogHeader>
                        <AlertDialogBody>
                            Verrà inviata una email di reset password al dipendente. Continuare?
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={resetPasswordCancelRef} onClick={onResetPasswordClose}>
                                Annulla
                            </Button>
                            <Button
                                backgroundColor="#688CC8"
                                color="white"
                                onClick={handleConfirmResetPassword}
                                ml={3}
                                isLoading={isResettingPassword}
                                _hover={{ bg: 'gray.700' }}
                                _active={{ bg: 'gray.600' }}
                                _focus={{ boxShadow: 'none' }}
                            >
                                Invia
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

            {/* Create Employee Modal */}
            <Modal isOpen={isCreateModalOpen} onClose={closeCreateModal}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader color="#9FB50C">Nuovo Dipendente</ModalHeader>
                    <ModalCloseButton _focus={{ boxShadow: 'none' }} />
                    <ModalBody>
                        <FormControl isInvalid={!!firstNameError}>
                            <Text fontWeight="bold">
                                Nome <Text as="span" color="red">*</Text>
                            </Text>
                            <Input
                                _focus={{ boxShadow: 'black' }}
                                placeholder="Nome"
                                width="100%"
                                value={firstName}
                                onChange={(e) => {
                                    setFirstName(e.target.value);
                                    if (firstNameError) setFirstNameError('');
                                }}
                            />
                            {firstNameError && (
                                <FormErrorMessage>{firstNameError}</FormErrorMessage>
                            )}
                        </FormControl>
                        <FormControl isInvalid={!!lastNameError} mt="4">
                            <Text fontWeight="bold">
                                Cognome <Text as="span" color="red">*</Text>
                            </Text>
                            <Input
                                _focus={{ boxShadow: 'black' }}
                                placeholder="Cognome"
                                width="100%"
                                value={lastName}
                                onChange={(e) => {
                                    setLastName(e.target.value);
                                    if (lastNameError) setLastNameError('');
                                }}
                            />
                            {lastNameError && (
                                <FormErrorMessage>{lastNameError}</FormErrorMessage>
                            )}
                        </FormControl>
                        <FormControl isInvalid={!!emailError} mt="4">
                            <Text fontWeight="bold">
                                Email <Text as="span" color="red">*</Text>
                            </Text>
                            <Input
                                _focus={{ boxShadow: 'black' }}
                                type="email"
                                placeholder="Email"
                                width="100%"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    if (emailError) setEmailError('');
                                }}
                            />
                            {emailError && (
                                <FormErrorMessage>{emailError}</FormErrorMessage>
                            )}
                        </FormControl>
                        <FormControl isInvalid={!!phoneError} mt="4">
                            <Text fontWeight="bold">Telefono</Text>
                            <Input
                                _focus={{ boxShadow: 'black' }}
                                placeholder="Es: +39 328 1234567"
                                width="100%"
                                value={phoneNumber}
                                onChange={(e) => {
                                    setPhoneNumber(e.target.value);
                                    if (phoneError) setPhoneError('');
                                }}
                            />
                            {phoneError && (
                                <FormErrorMessage>{phoneError}</FormErrorMessage>
                            )}
                        </FormControl>
                        <FormControl isInvalid={!!dateError} mt="4">
                            <Text fontWeight="bold">Data di Nascita</Text>
                            <Input
                                _focus={{ boxShadow: 'black' }}
                                type="date"
                                width="100%"
                                value={dateOfBirth}
                                onChange={(e) => {
                                    setDateOfBirth(e.target.value);
                                    if (dateError) setDateError('');
                                }}
                            />
                            {dateError && (
                                <FormErrorMessage>{dateError}</FormErrorMessage>
                            )}
                        </FormControl>
                    </ModalBody>
                    <ModalFooter>
                        <Tooltip
                            label="Riempi tutti i campi obbligatori"
                            isDisabled={firstName && lastName && email && !firstNameError && !lastNameError && !emailError ? true : false}
                            shouldWrapChildren
                        >
                            <Button
                                backgroundColor="#688CC8"
                                color="white"
                                mr={3}
                                onClick={handleCreateEmployee}
                                isLoading={isSubmitting}
                                disabled={!firstName || !lastName || !email}
                            >
                                Salva
                            </Button>
                        </Tooltip>
                        <Button colorScheme="gray" onClick={closeCreateModal}>
                            Annulla
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Edit Employee Modal */}
            <Modal isOpen={isEditModalOpen} onClose={closeEditModal}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader color="#9FB50C">Modifica Dipendente</ModalHeader>
                    <ModalCloseButton _focus={{ boxShadow: 'none' }} />
                    <ModalBody>
                        <FormControl isInvalid={!!editFirstNameError}>
                            <Text fontWeight="bold">
                                Nome <Text as="span" color="red">*</Text>
                            </Text>
                            <Input
                                _focus={{ boxShadow: 'black' }}
                                placeholder="Nome"
                                width="100%"
                                value={editFirstName}
                                onChange={(e) => {
                                    setEditFirstName(e.target.value);
                                    if (editFirstNameError) setEditFirstNameError('');
                                }}
                            />
                            {editFirstNameError && (
                                <FormErrorMessage>{editFirstNameError}</FormErrorMessage>
                            )}
                        </FormControl>
                        <FormControl isInvalid={!!editLastNameError} mt="4">
                            <Text fontWeight="bold">
                                Cognome <Text as="span" color="red">*</Text>
                            </Text>
                            <Input
                                _focus={{ boxShadow: 'black' }}
                                placeholder="Cognome"
                                width="100%"
                                value={editLastName}
                                onChange={(e) => {
                                    setEditLastName(e.target.value);
                                    if (editLastNameError) setEditLastNameError('');
                                }}
                            />
                            {editLastNameError && (
                                <FormErrorMessage>{editLastNameError}</FormErrorMessage>
                            )}
                        </FormControl>
                        <FormControl isInvalid={!!editEmailError} mt="4">
                            <Text fontWeight="bold">
                                Email <Text as="span" color="red">*</Text>
                            </Text>
                            <Input
                                _focus={{ boxShadow: 'black' }}
                                type="email"
                                placeholder="Email"
                                width="100%"
                                value={editEmail}
                                onChange={(e) => {
                                    setEditEmail(e.target.value);
                                    if (editEmailError) setEditEmailError('');
                                }}
                            />
                            {editEmailError ? (
                                <FormErrorMessage>{editEmailError}</FormErrorMessage>
                            ) : (
                                <Text fontSize="xs" color="gray.500" mt="1">
                                    Se modifichi l'email, al dipendente verranno inviate nuove credenziali di accesso a questo indirizzo.
                                </Text>
                            )}
                        </FormControl>
                        <FormControl isInvalid={!!editPhoneError} mt="4">
                            <Text fontWeight="bold">Telefono</Text>
                            <Input
                                _focus={{ boxShadow: 'black' }}
                                placeholder="Es: +39 328 1234567"
                                width="100%"
                                value={editPhoneNumber}
                                onChange={(e) => {
                                    setEditPhoneNumber(e.target.value);
                                    if (editPhoneError) setEditPhoneError('');
                                }}
                            />
                            {editPhoneError && (
                                <FormErrorMessage>{editPhoneError}</FormErrorMessage>
                            )}
                        </FormControl>
                        <FormControl isInvalid={!!editDateError} mt="4">
                            <Text fontWeight="bold">Data di Nascita</Text>
                            <Input
                                _focus={{ boxShadow: 'black' }}
                                type="date"
                                width="100%"
                                value={editDateOfBirth}
                                onChange={(e) => {
                                    setEditDateOfBirth(e.target.value);
                                    if (editDateError) setEditDateError('');
                                }}
                            />
                            {editDateError && (
                                <FormErrorMessage>{editDateError}</FormErrorMessage>
                            )}
                        </FormControl>
                    </ModalBody>
                    <ModalFooter>
                        <Tooltip
                            label="Riempi tutti i campi obbligatori"
                            isDisabled={editFirstName && editLastName && editEmail && !editFirstNameError && !editLastNameError && !editEmailError ? true : false}
                            shouldWrapChildren
                        >
                            <Button
                                backgroundColor="#688CC8"
                                color="white"
                                mr={3}
                                onClick={handleEditEmployee}
                                isLoading={editIsSubmitting}
                                disabled={!editFirstName || !editLastName || !editEmail}
                            >
                                Salva
                            </Button>
                        </Tooltip>
                        <Button colorScheme="gray" onClick={closeEditModal}>
                            Annulla
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
}
